import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ShieldCheck, MapPin, Clock, AlertTriangle, ChevronRight, Loader,
  Search, Building2, Moon, Minimize2, Car, BarChart3, Wrench, ClipboardList,
} from 'lucide-react'
import api from '../../services/api'
import { requestAndGetLocation } from '../../utils/geo'

const BG   = '#0D1117'
const CARD = '#161B22'
const NAVY = '#1D6AE5'

const FALLBACK_ACTIVITY_TYPES: Record<string, any> = {
  field_inspection:    { label: 'Field Inspection',    default_interval: 60 },
  remote_site:         { label: 'Remote Site Work',    default_interval: 30 },
  after_hours:         { label: 'After Hours Work',    default_interval: 60 },
  confined_space_prep: { label: 'Confined Space Prep', default_interval: 15 },
  driving:             { label: 'Lone Driving',        default_interval: 60 },
  data_collection:     { label: 'Data Collection',     default_interval: 60 },
  maintenance:         { label: 'Remote Maintenance',  default_interval: 30 },
  other:               { label: 'Other',               default_interval: 60 },
}

const ACTIVITY_CONFIG: Record<string, { icon: any; color: string; bg: string }> = {
  field_inspection:    { icon: Search,        color: '#3B82F6', bg: '#1E3A5F' },
  remote_site:         { icon: Building2,      color: '#F59E0B', bg: '#3B2A00' },
  after_hours:         { icon: Moon,           color: '#A78BFA', bg: '#2D1F4A' },
  confined_space_prep: { icon: Minimize2,      color: '#94A3B8', bg: '#1E2533' },
  driving:             { icon: Car,            color: '#10B981', bg: '#0D2E22' },
  data_collection:     { icon: BarChart3,      color: '#06B6D4', bg: '#0B2933' },
  maintenance:         { icon: Wrench,         color: '#FB923C', bg: '#2E1A0D' },
  other:               { icon: ClipboardList,  color: '#6B7280', bg: '#1A1E26' },
}

export default function LoneWorkerStart() {
  const navigate = useNavigate()
  const location = useLocation()
  const base = location.pathname.startsWith('/m/') ? '/m/lone-worker' : '/lone-worker'
  const [activityTypes, setActivityTypes] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [form, setForm] = useState({
    activity_type: '',
    location_description: '',
    hazard_notes: '',
    checkin_interval_minutes: 60,
    expected_duration_hours: 4,
  })
  const [gpsStatus, setGpsStatus] = useState<'idle' | 'getting' | 'ok' | 'denied'>('idle')
  const [gps, setGps] = useState<{ lat: number; lng: number } | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      api.get('/lone-worker/activity-types').catch(() => ({ data: null })),
      api.get('/lone-worker/active').catch(() => ({ data: null })),
    ]).then(([types, active]) => {
      setActivityTypes(types.data && Object.keys(types.data).length ? types.data : FALLBACK_ACTIVITY_TYPES)
      if (active.data) navigate(`${base}/active`, { replace: true })
    }).finally(() => setLoading(false))
  }, [])

  const getLocation = async () => {
    setGpsStatus('getting')
    try {
      const pos = await requestAndGetLocation()
      setGps(pos)
      setGpsStatus('ok')
    } catch {
      setGpsStatus('denied')
    }
  }

  const handleStart = async () => {
    if (!form.activity_type) { setError('Select an activity type'); return }
    setStarting(true); setError('')
    try {
      await api.post('/lone-worker/start', {
        ...form,
        ...(gps ? { latitude: gps.lat, longitude: gps.lng } : {}),
      })
      navigate(`${base}/active`)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start session')
      setStarting(false)
    }
  }

  const isMobile = base === '/m/lone-worker'

  if (loading) return (
    <div style={{ minHeight: isMobile ? '60vh' : '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Loader size={28} className="animate-spin" color={NAVY} />
    </div>
  )

  return (
    <div style={{ minHeight: isMobile ? 'auto' : '100vh', background: BG, color: '#E2E8F0', paddingBottom: 32 }}>
      {/* Header — hidden on mobile (MobileLayout provides it) */}
      {!isMobile && <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: '#1D3461', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ShieldCheck size={24} color={NAVY} />
        </div>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#F1F5F9', margin: 0, letterSpacing: '-0.02em' }}>Lone Worker Safety</h1>
          <p style={{ fontSize: 13, color: '#64748B', margin: '2px 0 0' }}>Start a monitored work session</p>
        </div>
      </div>}

      {/* Activity type */}
      <div style={{ padding: '0 16px 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>
          Activity Type <span style={{ color: '#EF4444' }}>*</span>
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {Object.entries(activityTypes).map(([key, cfg]: [string, any]) => {
            const sel = form.activity_type === key
            const ico = ACTIVITY_CONFIG[key] || ACTIVITY_CONFIG.other
            const Icon = ico.icon
            return (
              <button
                key={key}
                onClick={() => setForm(f => ({ ...f, activity_type: key, checkin_interval_minutes: cfg.default_interval }))}
                style={{
                  background: sel ? ico.bg : CARD,
                  border: `2px solid ${sel ? ico.color : '#21262D'}`,
                  borderRadius: 14,
                  padding: '14px 12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  transition: 'all 0.15s',
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: sel ? ico.color : '#21262D',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={19} color={sel ? '#fff' : ico.color} strokeWidth={2} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: sel ? '#F1F5F9' : '#94A3B8', lineHeight: 1.3 }}>
                  {cfg.label}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Location */}
      <div style={{ padding: '0 16px 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Location</p>
        <div style={{ background: CARD, border: '1px solid #21262D', borderRadius: 14, overflow: 'hidden' }}>
          <input
            value={form.location_description}
            onChange={e => setForm(f => ({ ...f, location_description: e.target.value }))}
            placeholder="e.g. Site A — northern boundary fence"
            style={{
              width: '100%', boxSizing: 'border-box', background: 'transparent',
              border: 'none', padding: '14px 16px', fontSize: 14, color: '#E2E8F0',
              outline: 'none',
            }}
          />
          <div style={{ height: 1, background: '#21262D' }} />
          <button
            onClick={getLocation}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '12px 16px', background: 'transparent', border: 'none',
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <MapPin size={16} color={gpsStatus === 'ok' ? '#10B981' : '#3B82F6'} />
            <span style={{ fontSize: 13, fontWeight: 600, color: gpsStatus === 'ok' ? '#10B981' : '#3B82F6' }}>
              {gpsStatus === 'idle'    && 'Capture GPS location'}
              {gpsStatus === 'getting' && 'Getting location…'}
              {gpsStatus === 'ok'      && `GPS captured (${gps?.lat.toFixed(4)}, ${gps?.lng.toFixed(4)})`}
              {gpsStatus === 'denied'  && 'Location denied — continue without GPS'}
            </span>
          </button>
        </div>
      </div>

      {/* Hazard notes */}
      <div style={{ padding: '0 16px 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Hazard Notes</p>
        <textarea
          value={form.hazard_notes}
          onChange={e => setForm(f => ({ ...f, hazard_notes: e.target.value }))}
          placeholder="Note any site-specific hazards or conditions…"
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box', background: CARD,
            border: '1px solid #21262D', borderRadius: 14, padding: '14px 16px',
            fontSize: 14, color: '#E2E8F0', resize: 'none', outline: 'none',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Duration + interval */}
      <div style={{ padding: '0 16px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            <Clock size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />Duration
          </p>
          <select
            value={form.expected_duration_hours}
            onChange={e => setForm(f => ({ ...f, expected_duration_hours: parseFloat(e.target.value) }))}
            style={{ width: '100%', background: CARD, border: '1px solid #21262D', borderRadius: 10, color: '#E2E8F0', padding: '10px 12px', fontSize: 14 }}
          >
            {[1, 2, 3, 4, 6, 8, 10, 12].map(h => <option key={h} value={h}>{h} hour{h !== 1 ? 's' : ''}</option>)}
          </select>
        </div>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Check-in Every</p>
          <select
            value={form.checkin_interval_minutes}
            onChange={e => setForm(f => ({ ...f, checkin_interval_minutes: parseInt(e.target.value) }))}
            style={{ width: '100%', background: CARD, border: '1px solid #21262D', borderRadius: 10, color: '#E2E8F0', padding: '10px 12px', fontSize: 14 }}
          >
            {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div style={{ margin: '0 16px 16px', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5', fontSize: 13 }}>
          <AlertTriangle size={16} />{error}
        </div>
      )}

      <div style={{ padding: '0 16px' }}>
        <button
          onClick={handleStart}
          disabled={starting}
          style={{
            width: '100%', padding: '16px', borderRadius: 16, border: 'none',
            background: starting ? '#21262D' : NAVY,
            color: starting ? '#475569' : '#fff',
            fontWeight: 800, fontSize: 16, cursor: starting ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'all 0.15s',
          }}
        >
          {starting
            ? <><Loader size={18} className="animate-spin" />Starting…</>
            : <>Start Session <ChevronRight size={20} /></>
          }
        </button>
      </div>
    </div>
  )
}
